// Scatter 累積計量的收集模擬（純邏輯，供 MockSpinService 產生免費遊戲的計量事件，並可單元測試）。
//
// 對應 Unity SlotStateMachine.CheckWin 的 FG Scatter 收集迴圈（見 docs/UNITY_SOURCE_ANALYSIS §1/§4）：
//   每收集到一顆 Scatter → 累積點數 +1 → 判定等級（scatterLevelFor）→
//   若「升級」或「count >= maxPoint」則發放一筆獎勵（升級用 [MOCK] 獎勵表、滿格後 retrigger 加免費次數）。
//
// [MOCK] 獎勵數值來源 asset 無資料（後端 RGS 決定），見 constants.ts 的 MOCK_SCATTER_BONUS 說明。

import {
  MOCK_SCATTER_BONUS,
  MOCK_SCATTER_RETRIGGER_SPINS,
  SCATTER_MAX_POINT,
  scatterLevelFor,
} from '../constants'
import type { ScatterBonus, ScatterCollectEvent } from '../net/SpinService'

/** collectScatters 的回傳：本轉的計量事件，以及推進後的計量狀態與本轉獎勵合計。 */
export interface ScatterCollectResult {
  /** 本轉逐顆 Scatter 的計量事件（依序播放）。 */
  readonly events: readonly ScatterCollectEvent[]
  /** 本轉結束後的累積點數。 */
  readonly endCount: number
  /** 本轉結束後已發放的獎勵次數（供下一轉續接 MOCK_SCATTER_BONUS 索引）。 */
  readonly awardIndexEnd: number
  /** 本轉獎勵合計的加倍率（併入 FG 累積倍率）。 */
  readonly addedMultiplier: number
  /** 本轉獎勵合計的加免費次數（retrigger）。 */
  readonly addedSpins: number
}

/**
 * 第 awardIndex 次發放的獎勵：前幾次走 [MOCK] 獎勵表，之後（滿格 retrigger）固定追加免費次數。
 * 對應 Unity 消費 ScatterBonusList[scatterBonusListIndex++] 的順序。
 */
function bonusForAward(awardIndex: number): ScatterBonus {
  const preset = MOCK_SCATTER_BONUS[awardIndex]
  if (preset !== undefined) return { addMultiplier: preset.addMultiplier, addSpinCount: preset.addSpinCount }
  return { addMultiplier: 0, addSpinCount: MOCK_SCATTER_RETRIGGER_SPINS }
}

/**
 * 模擬本次免費旋轉收集 landCount 顆 Scatter 的計量過程。
 * @param startCount   本轉開始前的累積點數。
 * @param landCount    本轉落地（收集）的 Scatter 數。
 * @param awardIndex   目前已發放的獎勵次數（跨轉累計）。
 */
export function collectScatters(startCount: number, landCount: number, awardIndex: number): ScatterCollectResult {
  const events: ScatterCollectEvent[] = []
  let count = startCount
  let awards = awardIndex
  let addedMultiplier = 0
  let addedSpins = 0

  for (let i = 0; i < landCount; i++) {
    const before = count
    count += 1
    const level = scatterLevelFor(count)
    const leveledUp = level > scatterLevelFor(before)

    // Unity：levelUp || count >= maxPoint 時發放一筆獎勵。
    if (leveledUp || count >= SCATTER_MAX_POINT) {
      const bonus = bonusForAward(awards)
      awards += 1
      addedMultiplier += bonus.addMultiplier
      addedSpins += bonus.addSpinCount
      events.push({ count, level, leveledUp, bonus })
    } else {
      events.push({ count, level, leveledUp })
    }
  }

  return { events, endCount: count, awardIndexEnd: awards, addedMultiplier, addedSpins }
}
